import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Modal from 'react-modal';
import { useNavigate, useLocation } from 'react-router-dom';
import './PostedJobs.css';
import JobPostingPreview from './JobPosting/JobPostingPreview';
import {
  Search,
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Briefcase,
  SlidersHorizontal,
  Building2,
  Filter,
  Loader2,
  X,
  Users,
  Pencil,
  Trash2,
  Star,
  Clock,
  IndianRupee,
  TrendingUp,
  TrendingDown,
} from 'lucide-react';

Modal.setAppElement('#root');

const ITEMS_PER_PAGE = 10;

const PostedJobs = ({ onEditClick, onViewApplicants, providedId, isRecruiter }) => {
  const [postings, setPostings] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [statusFilter, setStatusFilter] = useState('Open');
  const [tierFilter, setTierFilter] = useState('All');
  const [searchTerm, setSearchTerm] = useState('');

  const [sortField, setSortField] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [currentPage, setCurrentPage] = useState(1);

  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);

  const navigate = useNavigate();
  const location = useLocation();

  // 🚨 Determine if the current user is a Customer
  const isCustomerLogin = !!localStorage.getItem('customerId');

  useEffect(() => {
    const fetchPostings = async () => {
      let url = '';

      if (providedId) {
        setIsLoading(true);
        url = `${process.env.REACT_APP_API_BASE_URL}/api/job-postings?posterId=${providedId}&posterRole=Customer`;
      } else if (isRecruiter) {
        setIsLoading(true);
        url = `${process.env.REACT_APP_API_BASE_URL}/api/job-postings`;
      } else {
        return;
      }

      try {
        const response = await axios.get(url);
        setPostings(response.data);
      } catch (error) {
        console.error('Failed to fetch job postings:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPostings();
  }, [providedId, isRecruiter]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, tierFilter, sortField, sortDir]);

  const openModal = (job) => {
    setSelectedJob(job);
    setModalIsOpen(true);
  };

  const closeModal = () => {
    setModalIsOpen(false);
    setSelectedJob(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to permanently delete this job post?')) {
      try {
        await axios.delete(`${process.env.REACT_APP_API_BASE_URL}/api/job-postings/${id}`);
        setPostings(postings.filter((p) => p._id !== id));
        closeModal();
      } catch (error) {
        console.error('Failed to delete job post:', error);
      }
    }
  };

  const handleEdit = (id) => {
    closeModal();
    navigate(`${location.pathname}?id=${id}`);
    onEditClick('Job Posting');
  };

  const handleViewApplicants = (jobId, jobRole, posterRole) => {
    closeModal();
    onViewApplicants({ jobId, jobRole, posterRole }); 
  };

  const getJobStatus = (applyByDate, applyByTime) => {
    if (!applyByDate) return 'Open';
    const applyDateTimeString = `${applyByDate}T${applyByTime || '23:59:59'}`;
    const applyDateTime = new Date(applyDateTimeString);
    const now = new Date();
    return now < applyDateTime ? 'Open' : 'Closed';
  };

  const formatApplyBy = (applyByDate, applyByTime) => {
    if (!applyByDate) return 'N/A';
    try {
      const date = new Date(applyByDate);
      const options = { day: '2-digit', month: 'short', year: 'numeric' };
      const formattedDate = date.toLocaleDateString('en-GB', options);
      return applyByTime ? `${formattedDate} by ${applyByTime}` : formattedDate;
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const parseCTC = (ctcValue) => {
    if (!ctcValue) return 0;
    const str = String(ctcValue).replace(/[^\d.]/g, '');
    return parseFloat(str) || 0;
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const processed = useMemo(() => {
    let list = postings
      .filter((post) => getJobStatus(post.applyByDate, post.applyByTime) === statusFilter)
      .filter((post) => {
        if (!isRecruiter || tierFilter === 'All') return true;
        if (tierFilter === 'Premium') return post.isPremiumJob;
        if (tierFilter === 'Standard') return !post.isPremiumJob;
        return true;
      })
      .filter(
        (post) =>
          (post.companyName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (post.role || '').toLowerCase().includes(searchTerm.toLowerCase())
      );

    if (sortField) {
      list = [...list].sort((a, b) => {
        if (sortField === 'ctc') {
          const aV = parseCTC(a.ctc);
          const bV = parseCTC(b.ctc);
          return sortDir === 'asc' ? aV - bV : bV - aV;
        }
        if (sortField === 'company') {
          const aV = (a.companyName || '').toLowerCase();
          const bV = (b.companyName || '').toLowerCase();
          return sortDir === 'asc' ? aV.localeCompare(bV) : bV.localeCompare(aV);
        }
        if (sortField === 'applyBy') {
          const aV = a.applyByDate ? new Date(a.applyByDate).getTime() : 0;
          const bV = b.applyByDate ? new Date(b.applyByDate).getTime() : 0;
          return sortDir === 'asc' ? aV - bV : bV - aV;
        }
        return 0;
      });
    }

    return list;
  }, [postings, searchTerm, statusFilter, tierFilter, sortField, sortDir, isRecruiter]);

  const totalPages = Math.ceil(processed.length / ITEMS_PER_PAGE);
  const paginated = processed.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE
  );

  const getPageNums = () => {
    const pages = [];
    const max = 5;
    let start = Math.max(1, currentPage - Math.floor(max / 2));
    let end = Math.min(totalPages, start + max - 1);
    if (end - start < max - 1) start = Math.max(1, end - max + 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const SortIcon = ({ field }) => {
    const active = sortField === field;
    return (
      <span className="pj-sort-icons">
        <ChevronUp size={11} className={active && sortDir === 'asc' ? 'pj-si-on' : ''} />
        <ChevronDown size={11} className={active && sortDir === 'desc' ? 'pj-si-on' : ''} />
      </span>
    );
  };

  const totalCols = isRecruiter ? 11 : 7;

  if (isLoading) {
    return (
      <div className="pj-root">
        <div className="pj-loading">
          <div className="pj-loading-card">
            <Loader2 className="pj-spin" size={36} />
            <span>Loading posted jobs…</span>
          </div>
        </div>
      </div>
    );
  }

  // 🚨 THE FIX: Determine if the specific job clicked belongs to a Customer 🚨
  const isCustomerJob = String(selectedJob?.posterRole || '').toLowerCase().trim() === 'customer';
  // Allow edit/delete ONLY if the user is a Customer, OR if it's a Recruiter job
  const canModifyJob = isCustomerLogin || !isCustomerJob;

  return (
    <div className="pj-root">
      <div className="pj-bg-blob pj-blob-1" />
      <div className="pj-bg-blob pj-blob-2" />

      <div className="pj-header">
        <div className="pj-header-left">
          <div className="pj-title-icon">
            <Briefcase size={26} />
          </div>
          <div>
            <h1 className="pj-title">Posted Jobs</h1>
            <p className="pj-subtitle">
              {isRecruiter
                ? 'Manage all job postings on the platform'
                : 'Your published job postings'}
            </p>
          </div>
        </div>
        <span className="pj-count-badge">
          <Briefcase size={14} />
          {processed.length} job{processed.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="pj-controls">
        <div className="pj-search-wrap">
          <Search size={16} className="pj-search-icon" />
          <input
            className="pj-search"
            type="text"
            placeholder="Search by company or role…"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          {searchTerm && (
            <button className="pj-clear-btn" onClick={() => setSearchTerm('')}>
              <X size={12} />
            </button>
          )}
        </div>

        <div className="pj-filter-group">
          <span className="pj-filter-label">
            <Filter size={13} /> Status
          </span>
          <button
            className={`pj-filter-btn ${statusFilter === 'Open' ? 'active pj-open' : ''}`}
            onClick={() => setStatusFilter('Open')}
          >
            Open
          </button>
          <button
            className={`pj-filter-btn ${statusFilter === 'Closed' ? 'active pj-closed' : ''}`}
            onClick={() => setStatusFilter('Closed')}
          >
            Closed
          </button>
        </div>

        {isRecruiter && (
          <div className="pj-filter-group">
            <span className="pj-filter-label">
              <Star size={13} /> Tier
            </span>
            <button
              className={`pj-filter-btn ${tierFilter === 'All' ? 'active pj-tier-all' : ''}`}
              onClick={() => setTierFilter('All')}
            >
              All
            </button>
            <button
              className={`pj-filter-btn ${
                tierFilter === 'Premium' ? 'active pj-tier-premium' : ''
              }`}
              onClick={() => setTierFilter('Premium')}
            >
              <Star size={11} /> Premium
            </button>
            <button
              className={`pj-filter-btn ${
                tierFilter === 'Standard' ? 'active pj-tier-std' : ''
              }`}
              onClick={() => setTierFilter('Standard')}
            >
              Standard
            </button>
          </div>
        )}

        <div className="pj-filter-group">
          <span className="pj-filter-label">
            <SlidersHorizontal size={13} /> Sort
          </span>
          <button
            className={`pj-sort-btn ${sortField === 'ctc' ? 'active' : ''}`}
            onClick={() => handleSort('ctc')}
          >
            <IndianRupee size={13} />
            CTC
            {sortField === 'ctc' ? (
              sortDir === 'asc' ? (
                <TrendingUp size={13} />
              ) : (
                <TrendingDown size={13} />
              )
            ) : (
              <SortIcon field="ctc" />
            )}
          </button>
          <button
            className={`pj-sort-btn ${sortField === 'applyBy' ? 'active' : ''}`}
            onClick={() => handleSort('applyBy')}
          >
            <Clock size={13} />
            Apply By Date
            <SortIcon field="applyBy" />
          </button>
          <button
            className={`pj-sort-btn ${sortField === 'company' ? 'active' : ''}`}
            onClick={() => handleSort('company')}
          >
            <Building2 size={13} />
            Company
            <SortIcon field="company" />
          </button>
        </div>
      </div>

      <p className="pj-showing">
        {searchTerm
          ? `Showing ${paginated.length} of ${processed.length} results for "${searchTerm}"`
          : `Showing ${paginated.length} of ${processed.length} ${statusFilter.toLowerCase()} jobs`}
      </p>

      <div className="pj-card">
        <div className="pj-table-wrap">
          <table className="pj-table">
            <thead>
              <tr>
                <th className="pj-th-num">#</th>

                <th className="pj-th-sort" onClick={() => handleSort('company')}>
                  Company <SortIcon field="company" />
                </th>

                <th>Title</th>

                {isRecruiter && <th>Tier</th>}
                {isRecruiter && <th>Poster Role</th>}
                {isRecruiter && <th>Poster Name</th>}
                {isRecruiter && <th>Poster Email</th>}

                <th className="pj-th-sort" onClick={() => handleSort('applyBy')}>
                  Apply By Date <SortIcon field="applyBy" />
                </th>

                <th>Type</th>

                <th className="pj-th-sort" onClick={() => handleSort('ctc')}>
                  CTC / Stipend <SortIcon field="ctc" />
                </th>

                <th>Status</th>
              </tr>
            </thead>

            <tbody>
              {paginated.length === 0 ? (
                <tr>
                  <td colSpan={totalCols} className="pj-empty">
                    <Briefcase size={40} className="pj-empty-icon" />
                    <p className="pj-empty-title">
                      {searchTerm
                        ? `No jobs match "${searchTerm}"`
                        : `No ${statusFilter.toLowerCase()} jobs found`}
                    </p>
                    <p className="pj-empty-sub">Try adjusting your filters or search</p>
                  </td>
                </tr>
              ) : (
                paginated.map((post, idx) => {
                  const status = getJobStatus(post.applyByDate, post.applyByTime);
                  return (
                    <tr key={post._id} onClick={() => openModal(post)} className="pj-row">
                      <td className="pj-td-num">
                        {(currentPage - 1) * ITEMS_PER_PAGE + idx + 1}
                      </td>

                      <td className="pj-td-company">
                        <div className="pj-company-avatar">
                          {(post.companyName || '?')[0].toUpperCase()}
                        </div>
                        <span className="pj-company-name">{post.companyName}</span>
                      </td>

                      <td className="pj-td-role">{post.role}</td>

                      {isRecruiter && (
                        <td>
                          {post.isPremiumJob ? (
                            <span className="pj-tier-badge pj-tier-premium-badge">
                              <Star size={11} fill="currentColor" /> Premium
                            </span>
                          ) : (
                            <span className="pj-tier-badge pj-tier-std-badge">Standard</span>
                          )}
                        </td>
                      )}

                      {isRecruiter && (
                        <td>
                          <span
                            className={`pj-role-tag ${
                              post.posterRole === 'Customer'
                                ? 'pj-role-customer'
                                : 'pj-role-recruiter'
                            }`}
                          >
                            {post.posterRole || 'Recruiter'}
                          </span>
                        </td>
                      )}

                      {isRecruiter && (
                        <td className="pj-poster-name">
                          {post.postedByName || 'Unknown'}
                        </td>
                      )}

                      {isRecruiter && (
                        <td className="pj-poster-email">
                          {post.postedByEmail || 'No email available'}
                        </td>
                      )}

                      <td className="pj-td-secondary">
                        {formatApplyBy(post.applyByDate, post.applyByTime)}
                      </td>

                      <td>
                        <span className="pj-type-badge">{post.typeOfWork || 'N/A'}</span>
                      </td>

                      <td>
                        {post.ctc ? (
                          <span className="pj-ctc-badge">{post.ctc}</span>
                        ) : (
                          <span className="pj-td-dim">N/A</span>
                        )}
                      </td>

                      <td>
                        <span className={`pj-status pj-status-${status.toLowerCase()}`}>
                          {status}
                        </span>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="pj-pagination">
            <span className="pj-page-info">
              Page <strong>{currentPage}</strong> of <strong>{totalPages}</strong>
            </span>
            <div className="pj-page-controls">
              <button
                className="pj-page-btn nav"
                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                disabled={currentPage === 1}
              >
                <ChevronLeft size={14} />
              </button>
              {getPageNums().map((p) => (
                <button
                  key={p}
                  className={`pj-page-btn ${currentPage === p ? 'active' : ''}`}
                  onClick={() => setCurrentPage(p)}
                >
                  {p}
                </button>
              ))}
              <button
                className="pj-page-btn nav"
                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
              >
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        )}
      </div>

      {selectedJob && (
        <Modal
          isOpen={modalIsOpen}
          onRequestClose={closeModal}
          contentLabel="Job Details"
          className="pj-modal"
          overlayClassName="pj-overlay"
        >
          <div className="pj-modal-header">
            <div className="pj-modal-header-left">
              <div className="pj-modal-avatar">
                {(selectedJob.companyName || '?')[0].toUpperCase()}
              </div>
              <div>
                <h2 className="pj-modal-title">{selectedJob.role}</h2>
                <p className="pj-modal-company">{selectedJob.companyName}</p>
              </div>
            </div>
            <button onClick={closeModal} className="pj-modal-close">
              <X size={20} />
            </button>
          </div>

          <div className="pj-modal-body">
            <JobPostingPreview formData={selectedJob} />
          </div>

          <div className="pj-modal-footer">
            {/* 🚨 THE FIX: Only render Edit and Delete if permitted! 🚨 */}
            {canModifyJob && (
              <>
                <button
                  className="pj-modal-danger"
                  onClick={() => handleDelete(selectedJob._id)}
                >
                  <Trash2 size={15} /> Delete
                </button>
                <button
                  className="pj-modal-secondary"
                  onClick={() => handleEdit(selectedJob._id)}
                >
                  <Pencil size={15} /> Edit
                </button>
              </>
            )}

            <button
              className="pj-modal-primary"
              onClick={() => handleViewApplicants(selectedJob._id, selectedJob.role, selectedJob.posterRole)}
            >
              <Users size={15} /> View Applicants
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default PostedJobs;